import * as Dialog from "@radix-ui/react-dialog";
import { AlertTriangle, X } from "lucide-react";
import type { AiApiErrorResponse } from "@/lib/api-types";

function localDateTime(iso: string | undefined): string {
  if (!iso) return "the next reset";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "the next reset";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function localDate(iso: string | undefined): string {
  if (!iso) return "your next monthly reset";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "your next monthly reset";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "long" }).format(date);
}

function limitCopy(error: AiApiErrorResponse): { title: string; body: string } {
  switch (error.code) {
    case "RATE_LIMITED_DAILY":
      return {
        title: "Daily AI limit reached",
        body: `You've used all ${error.quota?.daily.limit ?? "your"} questions today. Resets at ${localDateTime(error.quota?.daily.resets_at_iso)}.`,
      };
    case "RATE_LIMITED_MONTHLY":
      return {
        title: "Monthly AI limit reached",
        body: `You've reached this month's ${error.quota?.monthly.limit ?? "available"} questions. Resets on ${localDate(error.quota?.monthly.resets_at_iso)}.`,
      };
    case "RATE_LIMITED_YEARLY":
      return {
        title: "Yearly AI limit reached",
        body: `You've reached your plan's ${error.quota?.yearly.limit ?? "available"} questions for the year. Resets when you renew.`,
      };
    case "AI_BUDGET_EXHAUSTED":
      return {
        title: "AI features paused",
        body: "AI features for this plan are paused for the rest of your subscription. They'll be back when you renew.",
      };
    default:
      return { title: "AI limit reached", body: error.status_message || "Please try again later." };
  }
}

export function TutorLimitModal({
  error,
  onClose,
}: {
  error: AiApiErrorResponse | null;
  onClose: () => void;
}) {
  const copy = error ? limitCopy(error) : null;

  return (
    <Dialog.Root open={Boolean(copy)} onOpenChange={(open) => { if (!open) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-[2px]" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-[81] w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-border bg-surface p-5 text-text shadow-2xl focus:outline-none">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-full bg-brand-soft text-brand">
              <AlertTriangle className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <Dialog.Title className="font-display text-lg font-semibold tracking-tight">
                {copy?.title}
              </Dialog.Title>
              <Dialog.Description className="mt-1.5 text-sm leading-relaxed text-text-muted">
                {copy?.body}
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="-mr-1 -mt-1 rounded-md p-1.5 text-text-muted transition-colors hover:bg-surface-2 hover:text-text"
                aria-label="Close limit notice"
              >
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>
          <Dialog.Close asChild>
            <button
              type="button"
              className="mt-5 h-10 w-full rounded-lg bg-brand px-4 text-sm font-medium text-white transition-opacity hover:opacity-95"
            >
              Got it
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
